import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { UXPContext } from '@core/context/Context';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import { getFontAwesomeIcon } from '@utils';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
import MobileSheet from '@widget/popups/MobileSheet';
import Icon from '@icon/Icon';
/**
 * @export
 * WizardStepIndicator - A horizontal step indicator for multi-step wizards
 *
 * Displays numbered steps in a horizontal scrollable container with:
 * - Numbered circles for each step
 * - Connector lines between steps
 * - Visual states: active, completed, error, pending
 * - Smooth auto-scroll to keep active step centered
 * - Theme-aware styling
 *
 * @example
 * ```tsx
 * <WizardStepIndicator
 *   steps={[
 *     { id: 'step1', label: 'Upload File', hasError: false },
 *     { id: 'step2', label: 'Map Fields', hasError: false },
 *     { id: 'step3', label: 'Review', hasError: false }
 *   ]}
 *   currentStepId="step2"
 *   completedSteps={['step1']}
 *   onStepClick={(stepId) => navigateToStep(stepId)}
 * />
 * ```
 *
 * @category Forms/Wizard
 */
export const WizardStepIndicator = ({ steps, currentStepId, onStepClick, completedSteps = [], disableAutoScroll = false }) => {
    const context = useContext(UXPContext);
    const activeStepRef = useRef(null);
    const containerRef = useRef(null);
    const isMobile = useIsMobileLayout();
    const [listOpen, setListOpen] = useState(false);
    // Get step index for display (1-indexed)
    const getStepNumber = (stepId) => {
        return steps.findIndex(s => s.id === stepId) + 1;
    };
    // Check if step is completed
    const isCompleted = (stepId) => {
        return completedSteps.includes(stepId);
    };
    // Check if step is active
    const isActive = (stepId) => {
        return stepId === currentStepId;
    };
    // Check if step is before the active step (for connector styling)
    const isBeforeActive = (stepId) => {
        const currentIndex = steps.findIndex(s => s.id === currentStepId);
        const stepIndex = steps.findIndex(s => s.id === stepId);
        return stepIndex < currentIndex;
    };
    // Smooth scroll to center the active step (only if not disabled)
    useEffect(() => {
        if (!disableAutoScroll && activeStepRef.current && containerRef.current) {
            activeStepRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }, [currentStepId, disableAutoScroll]);
    // Handle step click
    const handleStepClick = (stepId) => {
        if (onStepClick && !isActive(stepId)) {
            onStepClick(stepId);
        }
    };
    // Phones: "Step 2 of 5  Label" with a progress bar; tapping it lists the steps
    if (isMobile) {
        const index = steps.findIndex(s => s.id === currentStepId);
        const current = steps[index];
        return _jsxs(_Fragment, { children: [_jsxs("button", { type: "button", className: classNames("uxpcore_wizardstep__compact", { "uxpcore_wizardstep__compact--error": current === null || current === void 0 ? void 0 : current.hasError }), onClick: () => setListOpen(true), children: [_jsxs("span", { className: "uxpcore_wizardstep__compact-line", children: [_jsx("span", { className: "uxpcore_wizardstep__compact-count", children: `${context.$L('uxp-core.text.step')} ${index + 1} ${context.$L('uxp-core.text.of')} ${steps.length}` }), _jsx("span", { className: "uxpcore_wizardstep__compact-label", children: current === null || current === void 0 ? void 0 : current.label })] }), _jsx("span", { className: "uxpcore_wizardstep__compact-bar", children: _jsx("span", { style: { width: `${steps.length ? ((index + 1) / steps.length) * 100 : 0}%` } }) })] }), _jsx(MobileSheet, { isOpen: listOpen, onClose: () => setListOpen(false), title: context.$L('uxp-core.text.steps'), children: _jsx("div", { className: "uxpcore_sheetlist", children: steps.map((step, i) => {
                            const completed = isCompleted(step.id);
                            const clickable = !!onStepClick && completed;
                            return _jsxs("button", { type: "button", className: classNames("uxpcore_sheetlist__row", "uxpcore_wizardstep__option", {
                                    "uxpcore_wizardstep__option--current": isActive(step.id),
                                    "uxpcore_wizardstep__option--completed": completed,
                                    "uxpcore_wizardstep__option--error": step.hasError,
                                }), disabled: !clickable && !isActive(step.id), onClick: () => { setListOpen(false); if (clickable)
                                    handleStepClick(step.id); }, children: [_jsx("span", { className: "uxpcore_wizardstep__option-mark", children: completed ? _jsx(Icon, { icon: "phr check" }) : i + 1 }), _jsx("span", { className: "uxpcore_sheetlist__label", children: step.label })] }, step.id);
                        }) }) })] });
    }
    return (_jsx("div", { className: "uxpcore_wizardstep__container", ref: containerRef, children: _jsx("div", { className: "uxpcore_wizardstep__scroll-container", children: steps.map((step, index) => {
                const stepNumber = getStepNumber(step.id);
                const active = isActive(step.id);
                const completed = isCompleted(step.id);
                const hasError = step.hasError;
                const clickable = !!onStepClick;
                const showConnector = index < steps.length - 1;
                // green only between completed steps; the line after the current step is still to come
                const connectorActive = isBeforeActive(step.id);
                return (_jsxs(React.Fragment, { children: [_jsxs("div", { ref: active ? activeStepRef : null, className: `uxpcore_wizardstep__step ${active ? 'uxpcore_wizardstep__step--active' : ''} ${completed ? 'uxpcore_wizardstep__step--completed' : ''} ${hasError ? 'uxpcore_wizardstep__step--error' : ''} ${clickable ? 'uxpcore_wizardstep__step--clickable' : ''}`, onClick: () => handleStepClick(step.id), role: "button", tabIndex: clickable ? 0 : -1, "aria-current": active ? 'step' : undefined, "aria-label": `${context.$L('uxp-core.text.step')} ${stepNumber}: ${step.label}`, children: [_jsx("div", { className: "uxpcore_wizardstep__step-circle", children: completed ? (_jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon('fas check') })) : (_jsx("span", { children: stepNumber })) }), _jsx("div", { className: "uxpcore_wizardstep__step-label", children: step.label })] }), showConnector && (_jsx("div", { className: `uxpcore_wizardstep__connector ${connectorActive ? 'uxpcore_wizardstep__connector--active' : ''}` }))] }, step.id));
            }) }) }));
};
